import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { LogIn, Eye, EyeOff, UserPlus } from 'lucide-react';
import ReleaseNotesButton from '@/components/ReleaseNotesButton';
import {
  allowsRegistration,
  authLabel,
  dataLabel,
  isDemoAuthMode,
  isLocalSurface,
  isQaSurface,
  isSupabaseAuthMode,
  isSupabaseConfigured,
  surfaceLabel,
} from '@/config/runtime';
import { supabase } from '@/utils/supabase/client';

const MICROSOFT_SSO_ENABLED = isSupabaseAuthMode && isSupabaseConfigured;

export default function LoginPage() {
  const { login, register } = useAuth();
  const qaDemoMode = isQaSurface && isDemoAuthMode;
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [supabaseStatus, setSupabaseStatus] = useState('Verificando entorno...');

  useEffect(() => {
    let isMounted = true;

    async function checkSupabase() {
      if (isDemoAuthMode) {
        if (isMounted) {
          setSupabaseStatus(
            isQaSurface
              ? 'Modo QA demo activo. El acceso compartido opera sin backend remoto obligatorio.'
              : 'Modo local demo activo. La autenticacion y los datos se sirven localmente.',
          );
        }
        return;
      }

      if (!isSupabaseConfigured || !supabase) {
        if (isMounted) {
          setSupabaseStatus('La autenticacion remota no esta disponible en este entorno.');
        }
        return;
      }

      const { error: healthError } = await supabase
        .from('todos')
        .select('id', { count: 'exact', head: true });

      if (!isMounted) return;

      if (!healthError) {
        setSupabaseStatus('Supabase conectado correctamente. La autenticacion remota esta activa.');
        return;
      }

      if (healthError.code === 'PGRST205') {
        setSupabaseStatus('Supabase responde, pero los datos CRM siguen usando la capa local.');
        return;
      }

      setSupabaseStatus(
        isSupabaseAuthMode
          ? `Supabase configurado, pero la validacion devolvio: ${healthError.message}`
          : `Supabase configurado, pero la validacion devolvio: ${healthError.message}`,
      );
    }

    checkSupabase();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(name, email, password);
      } else {
        await login(email, password);
      }
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesion');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setError('');
    if (!supabase) {
      setError('La autenticacion remota no esta disponible en este entorno.');
      return;
    }
    setLoading(true);
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'openid email profile',
          redirectTo: `${window.location.origin}/`,
        },
      });
      // Si no hay error, el navegador se redirige a Microsoft; no hay que hacer nada mas.
      if (oauthError) {
        setError(oauthError.message || 'No fue posible iniciar sesion con Microsoft.');
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'No fue posible iniciar sesion con Microsoft.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div
        className="hidden lg:flex lg:w-1/2 relative items-end p-12"
        style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 45%, #2563EB 100%)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="relative z-10 text-white max-w-md">
          <h1 className="text-4xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Conecta<span className="text-blue-400">CRM</span>
          </h1>
          <p className="text-white/80 text-lg leading-relaxed">
            Gestiona tus clientes, oportunidades y actividades comerciales de forma inteligente.
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 bg-[#F8FAFC]">
        <Card className="w-full max-w-md border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <div className="mb-4 lg:hidden">
              <h1 className="text-2xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Conecta<span className="text-[#2563EB]">CRM</span>
              </h1>
            </div>
            <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {isRegister ? 'Crear cuenta' : 'Iniciar sesion'}
            </h2>
            <p className="text-sm text-[#475569]">
              {isRegister ? 'Registrate para comenzar tu prueba gratuita de 14 dias' : 'Ingresa tus credenciales para acceder'}
            </p>
            <div className={`mt-3 rounded-md border px-3 py-2 text-xs font-medium ${
              isDemoAuthMode
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-sky-200 bg-sky-50 text-sky-800'
            }`}>
              {`${surfaceLabel} | ${authLabel} | ${dataLabel}`}
            </div>
            <div className={`mt-2 rounded-md border px-3 py-2 text-xs ${
              isDemoAuthMode
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-sky-200 bg-sky-50 text-sky-800'
            }`}>
              {qaDemoMode
                ? 'QA demo: acceso compartido para pruebas. La autenticacion usa cuentas demo y el CRM sigue cargando datos locales.'
                : isLocalSurface
                  ? 'Local: puede usar cuentas demo y datos locales para iterar sin backend remoto.'
                  : 'Autenticacion y datos sobre Supabase. Registrate o inicia sesion para comenzar.'}
            </div>
            <div className="mt-3 flex justify-start">
              <ReleaseNotesButton />
            </div>
          </CardHeader>
          <CardContent>
            {MICROSOFT_SSO_ENABLED && (
              <div className="mb-4">
                <Button
                  type="button"
                  onClick={handleMicrosoftSignIn}
                  disabled={loading}
                  variant="outline"
                  className="w-full min-h-11 md:min-h-10 border-[#E2E8F0] text-[#0F172A] font-semibold hover:bg-[#F1F5F9] focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2"
                  data-testid="login-microsoft-button"
                >
                  <svg width="18" height="18" viewBox="0 0 23 23" className="mr-2" aria-hidden="true">
                    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                    <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                    <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                    <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                  </svg>
                  Iniciar sesion con Microsoft
                </Button>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-[#E2E8F0]" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-[#94A3B8] uppercase tracking-wider">o</span>
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Nombre</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre completo"
                    required={isRegister}
                    className="min-h-11 md:min-h-10 border-[#E2E8F0] focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                    data-testid="register-name-input"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Correo electronico</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.cl"
                  required
                  className="min-h-11 md:min-h-10 border-[#E2E8F0] focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Contrasena</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tu contrasena"
                    required
                    className="min-h-11 md:min-h-10 border-[#E2E8F0] pr-11 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                    className="absolute right-1 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-md text-[#94A3B8] hover:text-[#475569] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {error && (
                <div className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#EF4444]/20 rounded-md px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold min-h-11 md:min-h-10 focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : isRegister ? (
                  <><UserPlus size={16} className="mr-2" /> Crear cuenta</>
                ) : (
                  <><LogIn size={16} className="mr-2" /> Iniciar sesion</>
                )}
              </Button>
              {!allowsRegistration ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  El registro no esta habilitado en este entorno.
                </div>
              ) : (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setIsRegister(!isRegister); setError(''); }}
                    className="inline-flex min-h-11 items-center justify-center rounded-md px-2 text-sm text-[#2563EB] hover:text-[#1D4ED8] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2"
                    data-testid="toggle-auth-mode"
                  >
                    {isRegister ? 'Ya tienes cuenta? Inicia sesion' : 'No tienes cuenta? Registrate'}
                  </button>
                </div>
              )}
              <div className={`rounded-md border px-3 py-2 text-xs ${
                isDemoAuthMode
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-[#DBEAFE] bg-[#EFF6FF] text-[#1D4ED8]'
              }`}>
                {supabaseStatus}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
